import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';
import { createPaymentDiscount } from '../../../../../../../../crud/payments_api/payment_discounts';
import { Discount, TimeFrame } from '../../../../types';
import { toast } from 'react-toastify';
import { X } from 'react-bootstrap-icons';
import { FaInfoCircle, FaHandPaper, FaMagic } from 'react-icons/fa';
import './CreateDiscount.scss';
import DiscountType from './components/DiscountType/DiscountType';
import DiscountLimits from './components/DiscountLimits/DiscountLimits';
import DiscountReach from './components/DiscountReach/DiscountReach';
import TimeframeConfiguration from '../shared/TimeframeConfiguration/TimeframeConfiguration';

interface CreateDiscountProps {
  itemOptions: any[];
  menuOptions: any[];
  categoryOptions: any[];
  show: boolean;
  handleClose: () => void;
  setDiscount: any;
  discounts: Discount[];
  setShowCreateDiscountModal: any;
}

const CreateDiscount: React.FC<CreateDiscountProps> = ({
  show,
  handleClose,
  setDiscount,
  discounts,
  itemOptions,
  menuOptions,
  categoryOptions,
  setShowCreateDiscountModal
}) => {
  const [discountName, setDiscountName] = useState('');
  const [discountType, setDiscountType] = useState('');
  const [limitType, setLimitType] = useState('Sin limite');
  const [applyOn, setApplyOn] = useState('Global');

  const [isValid, setIsValid] = useState(true);
  const [maxNumber, setMaxNumber] = useState<number | null>(null);
  const [maxBudget, setMaxBudget] = useState<number | null>(null);
  const [categoryId, setCategoryId] = useState('');
  const [menuId, setMenuId] = useState('');
  const [itemId, setItemId] = useState('');
  const [localId, setLocalId] = useState<number | null>(null);
  const [discountAmount, setDiscountAmount] = useState<number | null>(null);
  const [discountPercentage, setDiscountPercentage] = useState<number | null>(null);
  const [amountError, setAmountError] = useState<string>('');

  // New multi-scope states
  const [itemIds, setItemIds] = useState<number[]>([]);
  const [categoryIds, setCategoryIds] = useState<number[]>([]);

  // Time-based scheduling states
  const [alwaysActive, setAlwaysActive] = useState(true);
  const [timeframes, setTimeframes] = useState<TimeFrame[]>([]);

  // Manual application state
  const [isManual, setIsManual] = useState(false);

  // Loading state for create button
  const [isCreating, setIsCreating] = useState(false);

  // Estados para el calendario interactivo
  const [selectedExpirationDate, setSelectedExpirationDate] = useState<Date | null>(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const [showInfoBanner, setShowInfoBanner] = useState(false);
  const [showAlcanceExplanation, setShowAlcanceExplanation] = useState('');

    // Opciones seleccionadas (si existen) - using itemOptions from utils (id, label format)
  const selectedCategory = categoryOptions.find((cat) => cat.id === categoryId);
  const selectedItem = itemOptions.find((itm) => itm.id === itemId);

  useEffect(() => {
    const userContext = localStorage.getItem('user');
    const localIdFromStorage = userContext ? JSON.parse(userContext).local_id : 0;
    setLocalId(localIdFromStorage);
  }, []);

  useEffect(() => {
    // Resetear campos al cerrar el modal de creación
    if (!show) {
      setDiscountName('');
      setSelectedExpirationDate(null);
      setDiscountType('');
      setLimitType('Sin limite');
      setApplyOn('Global');
      setIsValid(true);
      setMaxNumber(null);
      setMaxBudget(null);
      setCategoryId('');
      setMenuId('');
      setItemId('');
      setDiscountAmount(null);
      setDiscountPercentage(null);
      setShowCalendar(false);
      setShowInfoBanner(false);
      setAmountError('');
      setItemIds([]);
      setCategoryIds([]);
      setAlwaysActive(true);
      setTimeframes([]);
      setIsManual(false);
    }
  }, [show]);

  const handleCloseModal = () => {
    handleClose();
  };

  const handleCreateDiscount = async () => {
    setIsCreating(true);
    try {
      // Validate timeframes if not always active
      if (!alwaysActive && timeframes.length === 0) {
        toast.error('Debes configurar al menos un horario o activar "Siempre activo"');
        setIsCreating(false);
        return;
      }

      // Validate timeframes have at least one day selected
      if (!alwaysActive) {
        const invalidFrame = timeframes.find(frame => frame.days.length === 0);
        if (invalidFrame) {
          toast.error(`El horario "${invalidFrame.name}" debe tener al menos un día seleccionado`);
          setIsCreating(false);
          return;
        }
      }

      // Convertir la fecha seleccionada a formato "YYYY-MM-DD"
      const formattedExpiration = selectedExpirationDate && !isNaN(selectedExpirationDate.getTime())
        ? selectedExpirationDate.toISOString().split('T')[0]
        : '';
      
      const payload: any = {
        payment_discount_name: discountName,
        expiration_date: formattedExpiration === '' ? null : formattedExpiration,
        isvalid: isValid,
        max_number: maxNumber,
        max_budget: maxBudget,
        local_id: localId,
        payment_discount_amount: discountAmount,
        payment_discount_percentage: discountPercentage,
        // Time-based scheduling
        always_active: alwaysActive,
        // Manual application
        is_manual: isManual,
      };

      // Add timeframes config if not always active
      if (!alwaysActive && timeframes.length > 0) {
        payload.timeframes_config = {
          frames: timeframes.map(frame => ({
            name: frame.name,
            days: frame.days,
            start_time: frame.start_time,
            end_time: frame.end_time,
            active: frame.active ?? true
          }))
        };
      }

      // Handle scope based on applyOn selection
      if (applyOn === 'Global') {
        payload.isglobal = true;
      } else if (applyOn === 'Menú') {
        payload.menu_id = menuId === '' ? null : menuId;
      } else if (applyOn === 'Categoría') {
        payload.menu_category_id = categoryId === '' ? null : categoryId;
      } else if (applyOn === 'Item') {
        payload.item_id = itemId === '' ? null : itemId;
      } else if (applyOn === 'Multi-Item') {
        payload.item_ids = itemIds.length > 0 ? itemIds : null;
      } else if (applyOn === 'Multi-Categoría') {
        payload.category_ids = categoryIds.length > 0 ? categoryIds : null;
      }

      console.log('newDiscount payload:', payload);
      console.log('isManual state value:', isManual);

      const response = await createPaymentDiscount(payload);
      const newDiscount = (response as any).data;
      setDiscount([...discounts, newDiscount]);
      toast.success('Descuento creado exitosamente');
      handleClose();
    } catch (error: any) {
      if (error?.response?.data?.message === 'The discount amount is greater than the price of the item') {
        setAmountError('El monto del descuento no puede ser mayor o igual al valor del ítem seleccionado.');
      } else {
        console.error('Error al crear el descuento:', error);
        toast.error(error?.response?.data?.message || 'Error al crear el descuento');
      }
    } finally {
      setIsCreating(false);
    }
  };

  // Validación del monto - Note: Price validation removed to match CreateCoupon structure
  // Backend will handle validation of discount amount vs item price
  useEffect(() => {
    if (discountType === 'Amount' && discountAmount !== null) {
      // Basic validation for positive amounts
      if (discountAmount <= 0) {
        setAmountError('El monto del descuento debe ser mayor que 0.');
      } else {
        setAmountError('');
      }
    } else {
      setAmountError('');
    }
  }, [discountAmount, discountType]);

  return (
    <Modal show={show} onHide={handleCloseModal} className="modal-descuentos" size="lg">
      <Modal.Header>
        <div className="form-modal-title-container">
          <Modal.Title className="form-modal-title">Crear Descuento</Modal.Title>
        </div>
        <Button variant="none" className="modal-close-button" onClick={handleCloseModal}>
          <X size={24} />
        </Button>
      </Modal.Header>
      
      <Modal.Body>
        {/* Banner informativo */}
        <div className="discount-info-banner">
          <div className="banner-header" onClick={() => setShowInfoBanner(!showInfoBanner)}>
            <div className="header-content">
              <div className="header-icon">
                <FaInfoCircle />
              </div>
              <div className="header-title">¿Qué es un descuento?</div>
            </div>
            <div className={`toggle-icon ${showInfoBanner ? 'expanded' : 'collapsed'}`}>
              {showInfoBanner ? '−' : '+'}
            </div>
          </div>
          {showInfoBanner && (
            <div className="banner-content">
              Un descuento es una reducción aplicada automáticamente al precio de productos o servicios cuando están activos. 
              A diferencia de los cupones, los descuentos se aplican de forma <strong>automática</strong> a todos los clientes que cumplan las condiciones.
            </div>
          )}
        </div>

        <Form className="discount-form" onSubmit={(e) => { e.preventDefault(); handleCreateDiscount(); }}>
          {/* Nombre del descuento */}
          <div className="form-section name-section">
            <Form.Group className="mp-formgroup">
              <Form.Control
                type="text"
                value={discountName}
                onChange={(e) => setDiscountName(e.target.value)}
                required
                placeholder="Ingrese el descuento"
                className="discount-name-input"
              />
            </Form.Group>
          </div>

          {/* Tipo de descuento modularizado */}
          <DiscountType
            discountType={discountType}
            setDiscountType={setDiscountType}
            discountAmount={discountAmount}
            setDiscountAmount={setDiscountAmount}
            discountPercentage={discountPercentage}
            setDiscountPercentage={setDiscountPercentage}
            amountError={amountError}
          />

          <DiscountLimits
            limitType={limitType}
            setLimitType={setLimitType}
            selectedExpirationDate={selectedExpirationDate}
            setSelectedExpirationDate={setSelectedExpirationDate}
            showCalendar={showCalendar}
            setShowCalendar={setShowCalendar}
            maxBudget={maxBudget}
            setMaxBudget={setMaxBudget}
            maxNumber={maxNumber}
            setMaxNumber={setMaxNumber}
          />

          <DiscountReach
            applyOn={applyOn}
            setApplyOn={setApplyOn}
            menuOptions={menuOptions}
            categoryOptions={categoryOptions}
            itemOptions={itemOptions}
            menuId={menuId}
            setMenuId={setMenuId}
            categoryId={categoryId}
            setCategoryId={setCategoryId}
            itemId={itemId}
            setItemId={setItemId}
            showAlcanceExplanation={showAlcanceExplanation}
            setShowAlcanceExplanation={setShowAlcanceExplanation}
            selectedCategory={selectedCategory}
            selectedItem={selectedItem}
            itemIds={itemIds}
            setItemIds={setItemIds}
            categoryIds={categoryIds}
            setCategoryIds={setCategoryIds}
          />

          <TimeframeConfiguration
            alwaysActive={alwaysActive}
            setAlwaysActive={setAlwaysActive}
            timeframes={timeframes}
            setTimeframes={setTimeframes}
          />

          {/* Manual Application Section */}
          <div className="form-section application-mode-section">
            <div className="section-title">
              <FaHandPaper className="section-icon" />
              Modo de Aplicación
            </div>
            <div className="application-mode-options">
              <div
                className={`application-mode-option ${!isManual ? 'active' : ''}`}
                onClick={() => setIsManual(false)}
              >
                <div className="mode-header">
                  <div className="mode-icon">
                    <FaMagic />
                  </div>
                  <div className="mode-title">Automático</div>
                </div>
                <div className="mode-description">Se aplica automáticamente al generar la boleta</div>
              </div>
              <div
                className={`application-mode-option ${isManual ? 'active' : ''}`}
                onClick={() => setIsManual(true)}
              >
                <div className="mode-header">
                  <div className="mode-icon">
                    <FaHandPaper />
                  </div>
                  <div className="mode-title">Manual</div>
                </div>
                <div className="mode-description">El mesero o cajero debe aplicarlo manualmente</div>
              </div>
            </div>
          </div>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <div className="form-modal-footer-custom">
          <Button
            className="form-button-create"
            variant="none"
            disabled={isCreating}
            onClick={() => {
              if (!discountType) {
                toast.error('Debes seleccionar el tipo de descuento.');
                return;
              }
              if (!!amountError) {
                toast.error('Corrige el error en el monto del descuento.');
                return;
              }
              // Validation for multi-scope selections
              if (applyOn === 'Multi-Item' && itemIds.length === 0) {
                toast.error('Debes seleccionar al menos un ítem para el descuento multi-ítem.');
                return;
              }
              if (applyOn === 'Multi-Categoría' && categoryIds.length === 0) {
                toast.error('Debes seleccionar al menos una categoría para el descuento multi-categoría.');
                return;
              }
              // Validation for single-scope selections
              if (applyOn === 'Item' && !itemId) {
                toast.error('Debes seleccionar un ítem.');
                return;
              }
              if (applyOn === 'Categoría' && !categoryId) {
                toast.error('Debes seleccionar una categoría.');
                return;
              }
              handleCreateDiscount();
            }}
          >
            {isCreating ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Creando...
              </>
            ) : (
              'Crear Descuento'
            )}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default CreateDiscount;